import React, { useEffect } from 'react';
import { 
  Container, Box, Paper, Fade, TextField, Button, Typography, Alert,
  InputAdornment 
} from '@mui/material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';
import { useSwimming } from '../context/SwimmingContext';
import { useToast } from '../context/ToastContext';
import SortableSwimmer from '../components/swimmers/SortableSwimmer';
import DeleteConfirmationDialog from '../components/dialogs/DeleteConfirmationDialog';
import StopConfirmationDialog from '../components/dialogs/StopConfirmationDialog';
import { useCompetitionStates } from './Competition/states';
import { useCompetitionEffects } from './Competition/effects';
import { useCompetitionHandlers } from './Competition/handlers';
import { useSwimmersHandlers } from './Competition/swimmersHandlers';
import { STYLES } from './Competition/config';
import useWakeLock from '../hooks/useWakeLock';
import useVolumeButtons from '../hooks/useVolumeButtons';
import useMotionSensor from '../hooks/useMotionSensor';
import useDimScreen from '../hooks/useDimScreen';
import logoSwimteamPro from '../assets/logo_swimteampro.png';

function Competition() {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  const { isSupported, isActive } = useWakeLock();
  const { isDimmed } = useDimScreen();
  const { 
    swimmers, 
    setSwimmers, 
    addResult, 
    startCompetition, 
    endCompetition, 
    formatTime,
    updateResult,
    results,
    poolLength,
    raceDistance
  } = useSwimming();

  const {
    currentTime,
    setCurrentTime,
    totalTime,
    setTotalTime,
    startTime,
    setStartTime,
    totalStartTime,
    setTotalStartTime,
    isRunning,
    setIsRunning,
    isPaused,
    setIsPaused,
    pausedTime,
    setPausedTime,
    currentSwimmerId,
    setCurrentSwimmerId,
    swimmersData,
    setSwimmersData,
    swimmersList,
    setSwimmersList,
    newSwimmer,
    setNewSwimmer,
    hasFirstLap,
    setHasFirstLap,
    deleteConfirmation,
    setDeleteConfirmation,
    stopConfirmation,
    setStopConfirmation,
    isCompetitionEnded,
    setIsCompetitionEnded
  } = useCompetitionStates();

  const { hasUsedVolume } = useVolumeButtons(
    () => {
      if (isRunning && !isCompetitionEnded) {
        handleNextSwimmer();
      }
    },
    isRunning && !isCompetitionEnded
  );

  useMotionSensor(
    () => {
      if (isRunning && !isCompetitionEnded) {
        handleNextSwimmer();
      }
    },
    isRunning && !isCompetitionEnded
  );

  useEffect(() => {
    let interval;
    if (isRunning && !isPaused && !isCompetitionEnded) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - startTime;
        setCurrentTime(elapsed);
      }, 10);
    }
    return () => clearInterval(interval);
  }, [isRunning, isPaused, startTime, isCompetitionEnded, setCurrentTime]);

  const { sensors } = useCompetitionEffects({
    isRunning,
    isPaused,
    currentSwimmerId,
    startTime,
    currentTime,
    setCurrentTime,
    swimmersData,
    setSwimmersData,
    totalTime,
    totalStartTime,
    setTotalTime,
    isCompetitionEnded,
    results,
    poolLength,
    raceDistance
  });

  const {
    handleAddSwimmer,
    handleRemoveSwimmer,
    handleDragEnd,
    handleSwimmerDoubleClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditSwimmer
  } = useSwimmersHandlers({
    swimmersList,
    setSwimmersList,
    setSwimmers,
    setSwimmersData,
    setCurrentSwimmerId,
    currentSwimmerId,
    isRunning,
    newSwimmer,
    setNewSwimmer,
    deleteConfirmation,
    setDeleteConfirmation,
    swimmersData,
    updateResult,
    results,
    poolLength,
    raceDistance,
    showSuccess,
    showError
  });

  const {
    handleStart,
    handleStop,
    handleNextSwimmer,
    handleStopClick,
    handleStopConfirm,
    handleStopCancel
  } = useCompetitionHandlers({
    isRunning,
    setIsRunning,
    isPaused,
    setIsPaused,
    currentTime,
    setCurrentTime,
    startTime,
    setStartTime,
    totalStartTime,
    setTotalStartTime,
    pausedTime,
    setPausedTime,
    totalTime,
    setTotalTime,
    currentSwimmerId,
    setCurrentSwimmerId,
    swimmersList,
    swimmersData,
    setSwimmersData,
    hasFirstLap,
    setHasFirstLap,
    stopConfirmation,
    setStopConfirmation,
    isCompetitionEnded,
    setIsCompetitionEnded,
    startCompetition,
    endCompetition,
    addResult,
    results,
    poolLength,
    raceDistance,
    showSuccess,
    showError,
    showInfo
  });

  return (
    <Fade in={true}>
      <Container 
        maxWidth="lg"
        sx={{ 
          opacity: isDimmed ? 0.7 : 1,
          transition: 'opacity 1s ease-in-out',
          '&:hover, &:active, &:focus': {
            opacity: 1
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
            mt: 2
          }}
        >
          <Box
            component="img"
            src={logoSwimteamPro}
            alt="Logo SwimTeam Pro"
            sx={{
              width: 'auto',
              height: { xs: 40, sm: 50, md: 60 },
              objectFit: 'contain'
            }}
          />
        </Box>

        <Box sx={{ ...STYLES.mainContainer }}>
          {!isSupported && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                display: { xs: 'flex', md: 'none' } 
              }}
            >
              Para melhor experiência, mantenha a tela ativa durante a competição
            </Alert>
          )}

          <Box sx={{ 
            ...STYLES.headerBox, 
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 4, sm: 8 },
            mb: 3,
            position: 'relative',
            zIndex: 0,
            textAlign: 'center'
          }}>
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                Tempo Total:
              </Typography>
              <Typography variant="h6" color="primary">
                {formatTime(totalTime)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                Distância Total:
              </Typography>
              <Typography variant="h6" color="primary">
                {Object.values(swimmersData).reduce((total, swimmer) => {
                  const distance = swimmer.raceDistance || swimmer.poolLength || poolLength;
                  return total + ((swimmer.laps?.length || 0) * distance);
                }, 0)}m
              </Typography>
            </Box>
          </Box>

          <Paper elevation={3} sx={{ 
            ...STYLES.timerPaper, 
            position: 'relative',
            zIndex: 1,
            mt: { xs: 2, sm: 0 }
          }}>
            <Typography variant="h2" sx={STYLES.timerDisplay}>
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="h5" sx={STYLES.swimmerName}>
              Nadador Atual: {currentSwimmerId}
            </Typography>

            <Box sx={STYLES.buttonContainer}>
              {!isRunning ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleStart}
                  fullWidth
                  disabled={isCompetitionEnded}
                  sx={STYLES.startButton}
                >
                  Iniciar
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleStop}
                  sx={STYLES.stopButton}
                >
                  Parar
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNextSwimmer}
                disabled={!isRunning}
                color="success"
                sx={STYLES.nextButton}
              >
                Registrar Volta
              </Button>
            </Box>
          </Paper>

          <Box sx={{ ...STYLES.addSwimmerContainer }}>
            <TextField
              fullWidth
              label="Adicionar Novo Nadador"
              value={newSwimmer}
              onChange={(e) => setNewSwimmer(e.target.value)}
              variant="outlined"
              size="small"
              disabled={isCompetitionEnded}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newSwimmer.trim() && !isCompetitionEnded) {
                  handleAddSwimmer();
                }
              }}
              sx={{ 
                '& .MuiInputLabel-root': {
                  backgroundColor: '#fff',
                  padding: '0 4px'
                },
                '& .MuiInputLabel-shrink': {
                  backgroundColor: '#fff',
                  transform: 'translate(14px, -9px) scale(0.75)',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    top: 0
                  }
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddSwimmer}
              disabled={!newSwimmer.trim() || isCompetitionEnded}
              sx={STYLES.addButton}
            >
              Adicionar
            </Button>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={swimmersList}
              strategy={verticalListSortingStrategy}
            >
              {swimmersList.map((swimmer) => (
                <SortableSwimmer
                  key={swimmer}
                  swimmer={swimmer}
                  swimmersData={swimmersData}
                  currentSwimmer={currentSwimmerId}
                  formatTime={formatTime}
                  isRunning={isRunning}
                  isPaused={isPaused}
                  onDelete={() => {
                    if (!isRunning || swimmer !== currentSwimmerId) {
                      setDeleteConfirmation({ open: true, swimmer });
                    }
                  }}
                  onDoubleClick={(swimmerToEdit) => {
                    if (!isRunning || isPaused) {
                      handleSwimmerDoubleClick(swimmerToEdit);
                    }
                  }}
                  onEdit={(editData) => {
                    if (!isRunning || isPaused) {
                      handleEditSwimmer(editData);
                    }
                  }}
                  swimmersList={swimmersList}
                  poolLength={poolLength}
                  raceDistance={raceDistance}
                  disableEdit={isRunning && !isPaused}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Box sx={{ ...STYLES.actionButtonsContainer }}>
            {(totalTime > 0 || isRunning) && !isCompetitionEnded && (
              <Button
                variant="contained"
                color="error"
                onClick={handleStopClick}
                sx={STYLES.endButton}
              >
                Encerrar Competição
              </Button>
            )}
            {isCompetitionEnded && (
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate('/results')}
                sx={STYLES.resultsButton}
              >
                Ver Resultados
              </Button>
            )}
          </Box>

          <DeleteConfirmationDialog
            open={deleteConfirmation.open}
            swimmer={deleteConfirmation.swimmer}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />

          <StopConfirmationDialog
            open={stopConfirmation}
            onConfirm={handleStopConfirm}
            onCancel={handleStopCancel}
          />
        </Box>
      </Container>
    </Fade>
  );
}

export default Competition;